import type { CategoryCount, LocationCount, Item, TagCount } from "./types.js";

/**
 * Stage 5: "nobody feels locked in" -- one JSON shape for a whole inventory,
 * the same reason `buildListing()` (listing.ts) is its own function rather
 * than assembled inline on the screen that shows it.
 *
 * This is *not* a third exit alongside the Stage 3 prompt and the Stage 4
 * listing, and does not go through `publicFields()`: those two guard data
 * leaving the inventory to somewhere else (a model, a stranger's marketplace
 * post). An export is a member's own copy of exactly what they can already
 * see on every item screen -- private fields and notes included -- staying on
 * their own device. See docs/notes/invariants.md.
 */
export interface BackupManifest {
  exportedAt: number;
  inventoryId: string;
  itemCount: number;
  locations: LocationCount[];
  categories: CategoryCount[];
  tags: TagCount[];
  items: Item[];
}

export function buildBackupManifest(params: {
  inventoryId: string;
  locations: LocationCount[];
  categories: CategoryCount[];
  tags: TagCount[];
  items: Item[];
  exportedAt?: number;
}): BackupManifest {
  return {
    exportedAt: params.exportedAt ?? Date.now(),
    inventoryId: params.inventoryId,
    itemCount: params.items.length,
    locations: params.locations,
    categories: params.categories,
    tags: params.tags,
    items: params.items,
  };
}

/**
 * Where one photo lands inside the export zip, alongside `manifest.json`.
 * Keyed by the item id and the photo's own id -- both already unique, so
 * there is no title to slugify and nothing to collide, unlike the one-item
 * zip Stage 4's listing screen builds.
 */
export const backupPhotoPath = (itemId: string, photoId: string): string => `photos/${itemId}/${photoId}.jpg`;

/** The zip's own file name: short enough to read in a downloads folder, dated so a second export doesn't overwrite the first without asking. */
export function backupFileName(inventoryId: string, now: Date = new Date()): string {
  const date = now.toISOString().slice(0, 10);
  return `utucat-export-${inventoryId.slice(0, 8)}-${date}.zip`;
}
